import { useState } from "react";
import {
  MapPin,
  Globe,
  Instagram,
  FolderCheck,
  Sparkles,
  Check,
  Loader2,
  ArrowRight,
} from "lucide-react";

export type ServicePackage = {
  id: string;
  name: string;
  price: string;
  note?: string;
  delivery: string;
  icon: typeof MapPin;
  bullets: string[];
  featured?: boolean;
};

export const SERVICE_PACKAGES: ServicePackage[] = [
  {
    id: "google-profil",
    name: "Google-företagsprofil",
    price: "2 900 kr",
    note: "engångskostnad",
    delivery: "Klar inom 3 arbetsdagar",
    icon: MapPin,
    bullets: [
      "Upplägg eller övertagande av befintlig profil",
      "Rätt kategorier, öppettider och tjänster",
      "Bilder, logotyp och beskrivning som säljer",
      "Karta, vägbeskrivning och samtalsknapp",
      "Första inlägget publicerat",
    ],
  },
  {
    id: "synlighet-lopande",
    name: "Synlighet löpande",
    price: "990 kr/mån",
    note: "ingen bindningstid",
    delivery: "Startar direkt",
    icon: Sparkles,
    bullets: [
      "Inlägg på Google-profilen varje månad",
      "Svar på recensioner inom ett dygn",
      "Uppdaterade öppettider vid helger och event",
      "Månadsrapport med samtal, vägbeskrivningar och visningar",
    ],
  },
  {
    id: "hemsida-bokning",
    name: "Hemsida med bokning",
    price: "12 900 kr",
    note: "+ 349 kr/mån drift",
    delivery: "Klar inom 2 veckor",
    icon: Globe,
    bullets: [
      "1–4 sidor, byggd för mobil först",
      "Bokningssystem för bord eller sällskap",
      "Digital meny som du själv kan uppdatera",
      "Domän, e-postadresser och SSL",
      "Grundläggande SEO för din ort",
    ],
    featured: true,
  },
  {
    id: "meny-sociala",
    name: "Meny & sociala medier",
    price: "4 900 kr",
    note: "engångskostnad",
    delivery: "Klar inom 1 vecka",
    icon: Instagram,
    bullets: [
      "Digital meny med QR-kod för bordet",
      "Uppsättning av Instagram och Facebook",
      "10 färdiga startinlägg med bilder",
      "Enkel guide så du kan fortsätta själv",
    ],
  },
  {
    id: "dokumentpaket",
    name: "Dokumentpaket",
    price: "1 900 kr",
    note: "790 kr för premiummedlem",
    delivery: "Klar inom 2 arbetsdagar",
    icon: FolderCheck,
    bullets: [
      "Egenkontrollpärm i PDF och Word",
      "Genomgång och anpassning till din verksamhet",
      "Rutiner, loggar och checklistor för tillsyn",
    ],
  },
  {
    id: "startpaket",
    name: 'Startpaket "Öppna dörrarna"',
    price: "17 900 kr",
    note: "spara 2 800 kr",
    delivery: "Klar inom 3 veckor",
    icon: Sparkles,
    bullets: [
      "Google-företagsprofil",
      "Hemsida med bokning",
      "Digital meny med QR-kod",
      "En månad löpande synlighet på oss",
    ],
  },
];

export const DIGITAL_SERVICES_FAQS = [
  {
    q: "Vem levererar tjänsterna?",
    a: "Deniro Holdings, samma bolag som driver Alkoholprovet. Du har en kontaktperson genom hela leveransen.",
  },
  {
    q: "Måste jag ha serveringstillstånd först?",
    a: "Nej. Många börjar med Google-profil och hemsida redan under ansökan, så att verksamheten är synlig från dag ett.",
  },
  {
    q: "Vad kostar det att komma igång?",
    a: "Genomgången är kostnadsfri. Du får ett fast pris innan vi börjar, så det finns inga överraskningar.",
  },
  {
    q: "Kan jag ta över sajten själv sedan?",
    a: "Ja. Du äger domän och innehåll. Driftavgiften täcker hosting, uppdateringar och support – du kan avsluta när du vill.",
  },
  {
    q: "Hur snabbt hör ni av er?",
    a: "Vi svarar på förfrågningar inom en arbetsdag, oftast samma dag.",
  },
  {
    q: "Jobbar ni i hela Sverige?",
    a: "Ja, allt sker digitalt via mejl och video. Vi har kunder i hela landet.",
  },
];

export function ServicePackageGrid() {
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {SERVICE_PACKAGES.map((p) => {
        const Icon = p.icon;
        return (
          <div
            key={p.id}
            className={`flex flex-col rounded-2xl border bg-card p-6 shadow-card ${
              p.featured ? "border-primary/50 ring-1 ring-primary/20" : "border-border"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold">{p.name}</h3>
            </div>
            <div className="mt-4">
              <div className="font-display text-2xl font-semibold">{p.price}</div>
              {p.note && <div className="text-xs text-muted-foreground">{p.note}</div>}
            </div>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {p.bullets.map((b) => (
                <li key={b} className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">
              {p.delivery}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ServiceLeadForm({ source = "digitala-tjanster" }: { source?: string }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [services, setServices] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState<{
    name: string;
    email: string;
    services: string[];
  } | null>(null);

  const toggle = (id: string) =>
    setServices((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch("/api/public/leads/service-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(fd.get("name") ?? ""),
          company: String(fd.get("company") ?? ""),
          city: String(fd.get("city") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          message: String(fd.get("message") ?? ""),
          services,
          source,
          nickname: String(fd.get("nickname") ?? ""),
        }),
      });
      if (!res.ok) throw new Error("failed");
      setSubmitted({
        name: String(fd.get("name") ?? "").trim(),
        email: String(fd.get("email") ?? "").trim(),
        services,
      });
      setDone(true);
      form.reset();
      setServices([]);
    } catch {
      setErr("Något gick fel. Mejla oss på info@alkoholprovet.se istället.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    const steps = [
      "Vi tittar på hur synlig din verksamhet är på Google och webben i dag.",
      "Inom en arbetsdag, oftast samma dag, får du ett förslag med fast pris och leveranstid.",
      "Du bestämmer själv om vi kör. Genomgången kostar ingenting.",
    ];
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-2xl border border-success/40 bg-success/5 p-6"
      >
        <div className="flex items-center gap-2">
          <Check className="h-6 w-6 text-success" />
          <h3 className="font-display text-lg font-semibold">
            Tack{submitted?.name ? ` ${submitted.name}` : ""}! Din förfrågan är registrerad.
          </h3>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Vi har tagit emot ditt ärende och svarar till{" "}
          <strong className="text-foreground">{submitted?.email}</strong>.
        </p>
        {submitted && submitted.services.length > 0 && (
          <p className="mt-2 text-sm text-muted-foreground">
            Du är intresserad av:{" "}
            <span className="text-foreground">
              {submitted.services
                .map((id) => SERVICE_PACKAGES.find((p) => p.id === id)?.name ?? id)
                .join(" · ")}
            </span>
          </p>
        )}
        <h4 className="mt-5 text-sm font-semibold">Så går vi vidare</h4>
        <ol className="mt-2 space-y-2 text-sm">
          {steps.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success/15 text-xs font-semibold text-success">
                {i + 1}
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
        <p className="mt-5 text-sm text-muted-foreground">
          Vill du lägga till något? Mejla{" "}
          <a href="mailto:info@alkoholprovet.se" className="font-medium text-foreground underline">
            info@alkoholprovet.se
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => {
            setDone(false);
            setSubmitted(null);
          }}
          className="mt-4 text-sm font-medium underline"
        >
          Skicka en ny förfrågan
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <h3 className="font-display text-lg font-semibold">Få en kostnadsfri genomgång</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Berätta kort om din verksamhet – du får förslag och fast pris inom en arbetsdag.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Field label="Namn" name="name" required autoComplete="name" />
        <Field label="Verksamhet" name="company" autoComplete="organization" />
        <Field label="Ort" name="city" autoComplete="address-level2" />
        <Field label="Telefon" name="phone" type="tel" autoComplete="tel" />
        <div className="sm:col-span-2">
          <Field label="E-post" name="email" type="email" required autoComplete="email" />
        </div>
      </div>

      <fieldset className="mt-5">
        <legend className="text-sm font-medium">Vad är du intresserad av?</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {SERVICE_PACKAGES.map((p) => {
            const on = services.includes(p.name);
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(p.name)}
                className={`rounded-full border px-3 py-1.5 text-xs transition ${
                  on
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:bg-secondary"
                }`}
              >
                {p.name}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-5">
        <label htmlFor="sl-message" className="mb-1 block text-sm font-medium">
          Meddelande
        </label>
        <textarea
          id="sl-message"
          name="message"
          rows={4}
          maxLength={5000}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
          placeholder="Berätta gärna vad du behöver hjälp med och när du vill öppna."
        />
      </div>

      {/* honeypot */}
      <input
        type="text"
        name="nickname"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      {err && <p className="mt-3 text-sm text-destructive">{err}</p>}

      <button
        type="submit"
        disabled={busy}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
        Skicka förfrågan
      </button>
      <p className="mt-3 text-xs text-muted-foreground">
        Vi använder uppgifterna endast för att kontakta dig om din förfrågan.
      </p>
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  autoComplete,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  return (
    <div>
      <label htmlFor={`sl-${name}`} className="mb-1 block text-sm font-medium">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={`sl-${name}`}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        maxLength={255}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
      />
    </div>
  );
}
